/**
 * Canonical admin vocabularies. These are offline-fallback labels — the backend
 * serves the authoritative enum + `x-enum-labels` for every field at runtime via
 * `/admin/collection-schemas`. Keep them in sync with the Phase-1 docschema
 * (`backend/internal/docschema/schemas.go`).
 *
 * Y/DIRECTION (CMS plan D4) ships one true collection — Field Notes — whose only
 * closed enum is the article-body block `type`. A field note's `category` is a free
 * localized label (an {en,ar} leaf), not a queryable key, so it is NOT a vocabulary.
 */

export interface VocabOption {
  /** Stored canonical value. */
  value: string;
  /** i18n key for the human-facing label. */
  labelKey: string;
}

/**
 * Field-note article-body block types (`field_notes.doc.body[].type`). The closed
 * union the public renderer maps to known React elements — never raw HTML, so the
 * essay body is injection-safe by construction. Mirrors `fieldNoteDocSchema` (the
 * backend `block.type` enum); the localizer collapses the bilingual body to one
 * locale's block array before render.
 */
export const FIELD_NOTE_BLOCK_TYPES: VocabOption[] = [
  { value: 'paragraph', labelKey: 'vocab.block.paragraph' },
  { value: 'subheading', labelKey: 'vocab.block.subheading' },
  { value: 'list', labelKey: 'vocab.block.list' },
  { value: 'pullquote', labelKey: 'vocab.block.pullquote' },
  { value: 'aside', labelKey: 'vocab.block.aside' },
  { value: 'figure', labelKey: 'vocab.block.figure' },
];

/** Client roster type (`client.doc.type`) — external client vs Pharadev group brand. */
export const CLIENT_TYPES: VocabOption[] = [
  { value: 'external', labelKey: 'vocab.clientType.external' },
  { value: 'group', labelKey: 'vocab.clientType.group' },
];

/** Price-card row kind (`package.doc.kind`); `project` and `audit` rows are estimator bases. */
export const PACKAGE_KINDS: VocabOption[] = [
  { value: 'project', labelKey: 'vocab.packageKind.project' },
  { value: 'addon', labelKey: 'vocab.packageKind.addon' },
  { value: 'care', labelKey: 'vocab.packageKind.care' },
  { value: 'audit', labelKey: 'vocab.packageKind.audit' },
];

/** The product a project or audit sits under on /pricing (`package.doc.category`). */
export const PACKAGE_CATEGORIES: VocabOption[] = [
  { value: '', labelKey: 'vocab.packageCategory.none' },
  { value: 'website', labelKey: 'vocab.packageCategory.website' },
  { value: 'app', labelKey: 'vocab.packageCategory.app' },
  { value: 'service', labelKey: 'vocab.packageCategory.service' },
];

/** What a price-card row is priced per (`package.doc.price_unit`). */
export const PRICE_UNITS: VocabOption[] = [
  { value: 'project', labelKey: 'vocab.priceUnit.project' },
  { value: 'month', labelKey: 'vocab.priceUnit.month' },
  { value: 'each', labelKey: 'vocab.priceUnit.each' },
  { value: 'percent', labelKey: 'vocab.priceUnit.percent' },
];

/** Which /pricing estimator control an add-on drives (`package.doc.estimator.role`). */
export const ESTIMATOR_ROLES: VocabOption[] = [
  { value: '', labelKey: 'vocab.estimatorRole.none' },
  { value: 'integration', labelKey: 'vocab.estimatorRole.integration' },
  { value: 'mobile_app', labelKey: 'vocab.estimatorRole.mobileApp' },
  { value: 'inherited_code', labelKey: 'vocab.estimatorRole.inheritedCode' },
  { value: 'live_replacement', labelKey: 'vocab.estimatorRole.liveReplacement' },
];

/** Team-member department (`team_member.doc.department`). */
export const DEPARTMENTS: VocabOption[] = [
  { value: 'leadership', labelKey: 'vocab.department.leadership' },
  { value: 'marketing', labelKey: 'vocab.department.marketing' },
  { value: 'engineering', labelKey: 'vocab.department.engineering' },
];

/** Service card icon (`service.doc.icon`) — the closed set of Lucide glyph names. */
export const SERVICE_ICONS: VocabOption[] = [
  { value: 'Zap', labelKey: 'vocab.icon.zap' },
  { value: 'Code', labelKey: 'vocab.icon.code' },
  { value: 'Puzzle', labelKey: 'vocab.icon.puzzle' },
  { value: 'Brain', labelKey: 'vocab.icon.brain' },
  { value: 'Shield', labelKey: 'vocab.icon.shield' },
  { value: 'ClipboardCheck', labelKey: 'vocab.icon.clipboardCheck' },
  { value: 'Search', labelKey: 'vocab.icon.search' },
];

/** Portfolio industry key (`case_study.doc.industry_key`) — a public facet key. */
export const INDUSTRY_KEYS: VocabOption[] = [
  { value: 'restaurant', labelKey: 'vocab.industry.restaurant' },
  { value: 'mobile', labelKey: 'vocab.industry.mobile' },
  { value: 'ai_saas', labelKey: 'vocab.industry.aiSaas' },
  { value: 'publishing', labelKey: 'vocab.industry.publishing' },
  { value: 'industrial', labelKey: 'vocab.industry.industrial' },
  { value: 'education', labelKey: 'vocab.industry.education' },
];

/** Case-study hero device frame (`case_study.doc.device`). */
export const DEVICE_TYPES: VocabOption[] = [
  { value: 'laptop', labelKey: 'vocab.device.laptop' },
  { value: 'phone', labelKey: 'vocab.device.phone' },
];

/**
 * Service tier (`service.doc.tier`) — `core` services are the positioned offering
 * listed in full; `additional` ones render in the public "Also available" group.
 */
export const SERVICE_TIERS: VocabOption[] = [
  { value: 'core', labelKey: 'vocab.serviceTier.core' },
  { value: 'additional', labelKey: 'vocab.serviceTier.additional' },
];

/** Portfolio kind (`case_study.doc.kind`) — Pharadev's own product vs client work; a public facet key. */
export const PORTFOLIO_KINDS: VocabOption[] = [
  { value: 'product', labelKey: 'vocab.kind.product' },
  { value: 'client', labelKey: 'vocab.kind.client' },
];

/**
 * Field-note topic chip (`field_note.doc.topic`) — the blog index's problem-led
 * discovery filter. Tracks Pharadev's practice areas rather than service slugs,
 * so a chip survives a re-cut of the service catalogue. Mirrors the enum in
 * `fieldNoteDocSchema` (backend, authoritative).
 */
export const INSIGHT_TOPICS: VocabOption[] = [
  { value: 'automation', labelKey: 'vocab.topic.automation' },
  { value: 'digital-transformation', labelKey: 'vocab.topic.digitalTransformation' },
  { value: 'ai', labelKey: 'vocab.topic.ai' },
  { value: 'custom-software', labelKey: 'vocab.topic.customSoftware' },
  { value: 'integration', labelKey: 'vocab.topic.integration' },
  { value: 'security', labelKey: 'vocab.topic.security' },
];

/** Build the `x-enum-labels` map a JSON-schema enum field uses to show friendly text. */
export function enumLabels(options: VocabOption[]): Record<string, string> {
  return Object.fromEntries(options.map((o) => [o.value, o.labelKey]));
}

/** Build `{ value, label }` option list for a plain Select, resolving labels through t(). */
export function vocabSelectOptions(
  options: VocabOption[],
  t: (key: string) => string,
): { value: string; label: string }[] {
  return options.map((o) => ({ value: o.value, label: t(o.labelKey) }));
}
